import { useCallback } from 'react'
import { OrderFormat } from '@ancon/wildcat-types'
import noop from 'lodash/noop'

import useAppDispatch from '../../../store/hooks/useAppDispatch'
import useAppStore from '../../../store/hooks/useAppStore'
import { outletSelectedOutletTenantIdSelector } from '../../outlet/store/outletSelector'
import { outletsSelectedOutletListItemSelector } from '../../outlets/store/outletsSelectors'
import { fetchPreOrderSettings } from '../store/preOrderThunks'
import { preOrderSettingsSelector } from '../store/preOrderSelectors'
import { openNewOrderPanel } from '../store/preOrderSlice'
import { NewOrderPanelStep } from '../types'
import isCompanyOrderDateInRange from '../utils/isCompanyOrderDateInRange'
import getOutletToday from '../../outlet/utils/getOutletToday'

type StartNewCompanyOrderOptions = {
  orderFormat?: OrderFormat
  /** A day picked in the overview */
  orderDate?: string
  /** Ask for the date before the restaurant, as from the overview */
  isDateFirst?: boolean
}

/**
 * Opens the new company order panel with what is already known and at the
 * first step still to choose, after loading the brand's pre-order settings
 */
export default function useStartNewCompanyOrder() {
  const dispatch = useAppDispatch()
  const store = useAppStore()

  return useCallback(
    async ({
      orderFormat,
      orderDate: requestedOrderDate,
      isDateFirst = !!requestedOrderDate,
    }: StartNewCompanyOrderOptions = {}) => {
      if (outletSelectedOutletTenantIdSelector(store.getState())) {
        await dispatch(fetchPreOrderSettings()).unwrap().catch(noop)
      }

      const outlet = outletsSelectedOutletListItemSelector(store.getState())
      const hasRestaurant = !!outlet

      // A day outside the company calendar is picked again in the Date step
      const orderDate =
        requestedOrderDate &&
        isCompanyOrderDateInRange(
          requestedOrderDate,
          preOrderSettingsSelector(store.getState()),
          getOutletToday(outlet),
        )
          ? requestedOrderDate
          : undefined

      function getFirstOpenStep() {
        if (isDateFirst && !orderDate) {
          return NewOrderPanelStep.Date
        }

        if (isDateFirst) {
          return hasRestaurant
            ? NewOrderPanelStep.Format
            : NewOrderPanelStep.Restaurant
        }

        if (!hasRestaurant) {
          return NewOrderPanelStep.Restaurant
        }

        return orderFormat != null
          ? NewOrderPanelStep.Date
          : NewOrderPanelStep.Format
      }

      dispatch(
        openNewOrderPanel({
          isDateFirst,
          details: {
            orderFormat: orderFormat ?? null,
            orderDate: orderDate ?? null,
          },
          currentStep: getFirstOpenStep(),
        }),
      )
    },
    [dispatch, store],
  )
}
